import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import store, { borrowerActions } from '@apply/adminStore';
import { formatPhone } from '../../../common/SharedFunctions';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { BorrowerModals } from './BorrowerModals';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';

export const PrincipalEntitySelector = ({
    principalId,
    entityId,
    onSelect = null,
    disabled = false,
    required = true,
    canEdit = true,
}) => {
    const PER_PAGE = 20;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const { dispatch } = store;

    const [searchLoading, set_searchLoading] = useState(false);
    const [entId, set_entId] = useState(0);
    const [contactCache, set_contactCache] = useState({});
    const [principals, set_principals] = useState([]);
    const [principal, set_principal] = useState();
    const [principalQuery, set_principalQuery] = useState('');

    const [entities, set_entities] = useState([]);

    useEffect(() => {
        async function principalIdChanged() {
            if (!principalId) {
                set_principal(null);
                return;
            }

            const response = await borrowerApi.principalsTypeAheadById(principalId);
            if (response.statusCode !== 200) return;

            const selectedContact = response.result;
            onSelectContact(selectedContact);
        }

        principalIdChanged();
    }, [principalId]);

    useEffect(() => {
        set_entId(entityId);
    }, [entityId]);

    const populateEntities = async (principalId) => {
        if (!principalId) {
            set_entities([]);
            return;
        }
        const links = await getLinks(principalId);
        if (links.length > 0) {
            let selectEnt = null;
            if (entId > 0) {
                selectEnt = links.find((e) => e.entityId === entId);
            }
            if (!selectEnt && links.length === 1) {
                //Select First one
                const first = links[0];
                set_entId(first.entityId);
                onSelect && onSelect(principalId, first.entityId);
            }
        }
        set_entities(links);
    };

    const getLinks = async (principalId) => {
        if (!principalId) {
            return [];
        }

        const response = await borrowerApi.getPrincipalEntityLinks(principalId);
        if (response.statusCode !== 200) {
            return;
        }
        return response.result;
    };

    const doPrincipalsSearch = async (query, page = 1) => {
        const model = {
            searchText: query,
            take: PER_PAGE,
            skip: (page - 1) * PER_PAGE,
            excludedEntityId: 0,
        };
        return await borrowerApi.principalsTypeAhead(model);
    };

    const handleContactInputChange = (q) => {
        set_principal();
        q = (q ?? '').toLowerCase();
        set_principalQuery(q);
    };

    const handlePrincipalSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (contactCache[q]) {
            set_principals(contactCache[q].options);
            return;
        }

        set_searchLoading(true);
        doPrincipalsSearch(q).then((resp) => {
            contactCache[q] = { options: resp.result, page: 1 };
            set_contactCache(contactCache);
            set_searchLoading(false);
            set_principals(resp.result);
        });
    }, []);

    const handleContactPagination = (e, shownResults) => {
        const cachedQuery = contactCache[principalQuery];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);
        const page = cachedQuery.page + 1;
        doPrincipalsSearch(principalQuery, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            contactCache[principalQuery] = { ...cachedQuery, options, page };
            set_contactCache(contactCache);
            set_searchLoading(false);
            set_principals(options);
        });
    };

    const onAddContact = () => {
        dispatch(
            borrowerActions.setEditPrincipalModal({
                editPrincipalModal: {
                    isOpen: true,
                    principalId: 0,
                    onChange: onAfterContactChange,
                },
            })
        );
    };

    const onSelectContact = async (contact) => {
        set_principal(contact);
        await populateEntities(contact?.principalId);
    };

    const onClearContact = () => {
        set_principal(null);
        set_entities([]);
        onSelect && onSelect(null, null);
    };

    const onAfterContactChange = async (contact) => {
        set_contactCache({});
        await onSelectContact(contact);
    };

    const onEditPrincipal = (principalId) => {
        dispatch(
            borrowerActions.setEditPrincipalModal({
                editPrincipalModal: {
                    isOpen: true,
                    principalId,
                    onChange: onAfterContactChange,
                },
            })
        );
    };

    const onEntitySelect = (entityIdString) => {
        let entityId = parseInt(entityIdString);
        if (isNaN(entityId)) {
            entityId = null;
        }
        set_entId(entityId);
        onSelect && onSelect(principal?.principalId, entityId);
    };

    const filterByContactFields = ['name', 'email', 'userName'];

    return (
        <div>
            <div className='type-ahead-container'>
                <label htmlFor='borrower-search'>Borrower Principal {required && <span className='req'>*</span>}</label>
                <AsyncTypeahead
                    className='type-ahead'
                    id='borrower-search'
                    isLoading={searchLoading}
                    labelKey={(option) => `${option?.name}`}
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleContactInputChange}
                    onPaginate={handleContactPagination}
                    onSearch={handlePrincipalSearch}
                    onChange={(event) => onSelectContact(event[0])}
                    options={principals}
                    paginate
                    placeholder='Search on Name or Email...'
                    renderMenuItemChildren={(option) => (
                        <div
                            key={option.id}
                            style={{ borderBottom: '1px solid silver' }}
                        >
                            <span>
                                <div>
                                    <b>{option.name}</b> {option.email && <span>({option.email})</span>}
                                </div>

                                {option.city && (
                                    <div>
                                        {option.city}, {option.state}
                                    </div>
                                )}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={principal ? [principal] : []}
                    disabled={disabled}
                    inputProps={{ required: required }}
                    filterBy={filterByContactFields}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => onClearContact()}
                    disabled={disabled}
                    title='Remove selected Rep.'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <button
                    type='button'
                    onClick={onAddContact}
                    className='button type-ahead-add-button'
                    title='Add a new Rep.'
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>

            {principal && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        display: 'flex',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            width: '90%',
                            float: 'left',
                            paddingRight: '20px',
                            fontSize: '13px',
                            textAlign: 'left',
                        }}
                    >
                        {principal.name && (
                            <div>
                                <b>{principal.name}</b>
                            </div>
                        )}
                        {principal.street1 && <div>{principal.street1}</div>}
                        {principal.street2 && <div>{principal.street2}</div>}
                        {principal.city && principal.state && principal.zipCode && (
                            <div>
                                {principal.city}, {principal.state} {principal.zipCode}
                            </div>
                        )}
                        {principal.email && <div>{principal.email}</div>}
                        {principal.phone && <div>{formatPhone(principal.phone)}</div>}
                    </div>
                    {canEdit && (
                        <div
                            style={{
                                width: '10%',
                                float: 'right',
                                textAlign: 'right',
                            }}
                        >
                            <button
                                type='button'
                                onClick={() => onEditPrincipal(principal.principalId)}
                                className='button hollow'
                                title='Edit Contact Details'
                                style={{
                                    verticalAlign: 'unset',
                                    marginBottom: '0',
                                }}
                            >
                                <FontAwesomeIcon icon={faPencil} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div style={{ marginTop: '10px' }}>
                <label htmlFor='entityId'>Borrower Entity {required && <span className='req'>*</span>}</label>
                <select
                    id='entityId'
                    required={true}
                    value={entId}
                    onChange={(e) => onEntitySelect(e.target.value)}
                    disabled={!entities || entities.length === 0 || disabled}
                >
                    <option value=''>Select Entity...</option>
                    {entities &&
                        entities.map((e) => {
                            return (
                                <option
                                    key={e.entityId}
                                    value={e.entityId}
                                >
                                    {e.name}
                                </option>
                            );
                        })}
                </select>
            </div>

            <BorrowerModals />
        </div>
    );
};
